import {
  Flex,
  FormLabel,
  Switch,
  Tab as ChakraTab,
  TabPanel,
  Text,
} from '@chakra-ui/react';
import { PartOrderHistory } from '@snap-types/actions/part-order-history';
import { Decimal } from '@snap-types/types/decimal';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { BasicPagination } from '@ui/components';
import { QueryError, QueryLoading, QueryNoResults } from '@ui/components/query';
import { TanstackTable } from '@ui/components/TanstackTable';
import { useQuoteLineContext } from '@ui/features/quote-builder/components/QuoteLine/QuoteLineProvider';
import { useGetPartOrderHistoryQuery } from '@ui/features/quote-builder/state/endpoints/get-part-order-history';
import { actions } from '@ui/features/quote-builder/state/reducer';
import { selectQuoteBuilder } from '@ui/features/quote-builder/state/selectors/quote-builder';
import { selectQuoteLineWithLeadTimeIssues } from '@ui/features/quote-builder/state/selectors/quote-line';
import { selectQuoteLineInfo } from '@ui/features/quote-builder/state/selectors/quote-line-info';
import { QuoteInfoTab } from '@ui/features/quote-builder/types/state/quote-line';
import { usePaginationState } from '@ui/hooks/pagination';
import { useCurrentSite } from '@ui/hooks/useCurrentSite';
import { useCurrentUser } from '@ui/hooks/useCurrentUser';
import { useDispatch } from '@ui/hooks/useDispatch';
import { useSelector } from '@ui/hooks/useSelector';
import { currency, monthDayYear } from '@ui/util';
import { useMemo } from 'react';

import { TabComponentType } from './types';

const tab = QuoteInfoTab.OrderHistory;

function Tab() {
  const dispatch = useDispatch();
  const { quoteLineUuid } = useQuoteLineContext();

  function handleTabChange() {
    dispatch(actions.quoteLineInfoTabChanged({ quoteLineUuid, tab }));
  }

  return <ChakraTab onClick={handleTabChange}>Order History</ChakraTab>;
}

const perPageOptions = [10, 25, 50];

type OrderHistoryProps = {
  perPage?: number;
};

function OrderHistoryPanel({ perPage = 10 }: OrderHistoryProps) {
  const { quoteLineUuid } = useQuoteLineContext();
  const dispatch = useDispatch();

  const customerId = useSelector(
    (state) => selectQuoteBuilder(state).customerId,
  );

  const partId = useSelector(
    (state) => selectQuoteLineWithLeadTimeIssues(state, quoteLineUuid).partId,
  );

  const [paginationState, paginator] = usePaginationState({
    page: 1,
    perPage,
  });

  const onlyThisCustomerOrders = useSelector(
    (state) =>
      selectQuoteLineInfo(state, quoteLineUuid)
        .infoTabOrderHistoryFilterByCustomer,
  );

  const toggleOnlyThisCustomerOrders = () => {
    // When changing the filter, we should return to Page 1
    paginator.setPage(1);

    dispatch(
      actions.quoteLineInfoCustomerOnlyToggled({
        quoteLineUuid,
        tab,
      }),
    );
  };

  const query = useGetPartOrderHistoryQuery({
    siteCode: useCurrentSite().code,
    partId,
    page: paginationState.page,
    perPage: paginationState.perPage,
    customerId: onlyThisCustomerOrders ? customerId : undefined,
  });

  if (query.isLoading) {
    return <QueryLoading title="Loading order history..." />;
  }

  if (query.isError || !query.isSuccess) {
    return (
      <QueryError
        title="Error loading order history!"
        error={query.error}
        onRetry={() => query.refetch()}
        isRetrying={query.isFetching}
      />
    );
  }

  const noData = query.data.data.length === 0;

  return (
    <>
      <Flex gap={2} flexDirection="row">
        <Switch
          id="only-this-customer-orders"
          onChange={toggleOnlyThisCustomerOrders}
          isChecked={onlyThisCustomerOrders}
        />
        <FormLabel htmlFor="only-this-customer-orders">
          Only this customer's orders
        </FormLabel>
      </Flex>
      {noData ? (
        <QueryNoResults
          title={
            onlyThisCustomerOrders
              ? 'No orders found for this customer'
              : 'No order history found'
          }
        />
      ) : (
        <>
          <OrderHistoryTable
            data={query.data.data}
            isFetching={query.isFetching}
          />
          <BasicPagination
            metadata={query.data.pagination}
            paginator={paginator}
            perPageOptions={perPageOptions}
          />
        </>
      )}
    </>
  );
}

type OrderHistoryTableProps = {
  data: PartOrderHistory[];
  isFetching: boolean;
};

function OrderHistoryTable({ data, isFetching }: OrderHistoryTableProps) {
  const { isSuperAdmin } = useCurrentUser();

  const columns = useMemo(() => {
    const helper = createColumnHelper<PartOrderHistory>();

    return [
      helper.accessor('orderDate', {
        header: 'Order Date',
        enableSorting: false,
        cell: (cell) => monthDayYear(cell.getValue()?.toISOString()),
      }),

      helper.accessor('orderId', {
        header: 'Order ID',
        cell: (cell) => <Text fontWeight="bold">{cell.getValue()}</Text>,
      }),

      ...(isSuperAdmin
        ? [
            helper.accessor('lineNumber', {
              header: 'Line #',
              cell: (cell) => <Text fontWeight="bold">{cell.getValue()}</Text>,
            }),
          ]
        : []),

      helper.accessor('customerId', {
        header: 'Customer ID',
      }),

      helper.accessor('customerName', {
        header: 'Customer',
        cell: (cell) => (
          <Text maxWidth={200} noOfLines={1}>
            {cell.getValue()}
          </Text>
        ),
      }),

      helper.accessor('leadTime', {
        header: 'Lead Time',
      }),

      helper.accessor('quantity', {
        header: 'Quantity',
      }),

      helper.accessor('unitCost', {
        header: 'Unit Cost',
        cell: (cell) => {
          const value = cell.getValue();

          if (value) {
            return currency(value.toFixed(2));
          }
        },
      }),

      helper.accessor('unitPrice', {
        header: 'Unit Price',
        cell: (cell) => currency(cell.getValue()),
      }),

      helper.accessor('grossMargin', {
        header: 'Gross margin',
        cell: (cell) => {
          const value = cell.getValue();

          if (value && value.isFinite()) {
            if (value.isZero()) {
              return '--';
            }
            return `${new Decimal(value).times(100).toDecimalPlaces(2)}%`;
          } else {
            return 'No cost data';
          }
        },
      }),

      helper.accessor('contributionMargin', {
        header: 'Contribution Margin',
        cell: (cell) => {
          const value = cell.getValue();

          if (value && value.isFinite()) {
            if (value.isZero()) {
              return '--';
            }
            return `${new Decimal(value).times(100).toDecimalPlaces(2)}%`;
          } else {
            return 'No cost data';
          }
        },
      }),

      helper.accessor('desiredShipDate', {
        header: 'Desired Ship Date',
        cell: (cell) => monthDayYear(cell.getValue()?.toISOString()),
      }),

      helper.accessor('lastShippedDate', {
        header: 'Last Ship Date',
        cell: (cell) => monthDayYear(cell.getValue()?.toISOString()),
      }),
    ];
  }, [isSuperAdmin]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return <TanstackTable table={table} isLoading={isFetching} />;
}

function Panel() {
  return (
    <TabPanel>
      <OrderHistoryPanel />
    </TabPanel>
  );
}

export const OrderHistory = {
  tab,
  Tab,
  Panel,
} satisfies TabComponentType;
